import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { KeyRound, ArrowLeft, Eye, EyeOff, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) return setError("Password must be at least 8 characters");
    if (pw !== confirm) return setError("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav("/admin/login", { replace: true }), 2000);
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-stone-50">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1581141444721-0e6f8fa8397e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBtb3NxdWUlMjBhcmNoaXRlY3R1cmUlMjBleHRlcmlvcnxlbnwwfHx8fDE3ODA4MTg2Mzd8MA&ixlib=rb-4.1.0&q=85" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-emerald-950/70" />
        <div className="absolute inset-0 p-12 flex flex-col justify-end text-white">
          <div className="text-amber-300 text-6xl font-serif-en">ﷲ</div>
          <h1 className="font-serif-en text-5xl mt-4">Set New Password</h1>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} data-testid="reset-form" className="w-full max-w-sm">
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-amber-700 mb-3"><KeyRound className="w-3.5 h-3.5" /> Reset</div>
            <h2 className="font-serif-en text-3xl text-emerald-950">Choose a new password</h2>
            <p className="text-stone-500 mt-1 text-sm">Minimum 8 characters. This link can only be used once.</p>
          </div>

          {!token ? (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 text-sm text-red-700">
              No reset token provided. Please request a new reset link from the <Link to="/admin/forgot-password" className="underline">forgot password</Link> page.
            </div>
          ) : done ? (
            <div data-testid="reset-success" className="bg-emerald-50 border border-emerald-200 rounded-lg p-5">
              <CheckCircle2 className="w-6 h-6 text-emerald-700 mb-2" />
              <div className="font-medium text-emerald-900">Password updated</div>
              <p className="text-sm text-stone-700 mt-2">Redirecting to login…</p>
            </div>
          ) : (
            <>
              <label className="text-xs uppercase tracking-widest text-stone-500 mb-1 block">New Password</label>
              <div className="relative mb-4">
                <input data-testid="reset-pw" type={show ? "text" : "password"} value={pw} onChange={(e) => setPw(e.target.value)} className="w-full px-4 py-3 rounded-md border border-stone-300 focus:outline-none focus:ring-2 focus:ring-emerald-700 pr-10" minLength={8} required />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700">{show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
              </div>
              <label className="text-xs uppercase tracking-widest text-stone-500 mb-1 block">Confirm New Password</label>
              <input data-testid="reset-confirm" type={show ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="w-full px-4 py-3 mb-2 rounded-md border border-stone-300 focus:outline-none focus:ring-2 focus:ring-emerald-700" minLength={8} required />
              {error && <div data-testid="reset-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3 mt-3">{error}</div>}
              <button data-testid="reset-submit" disabled={busy} className="btn-primary w-full mt-6 disabled:opacity-60">{busy ? "Saving…" : "Set Password"}</button>
              <Link to="/admin/login" className="mt-4 text-sm text-stone-500 hover:text-emerald-900 inline-flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5" /> Back to login</Link>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
